import type { AiOperatingMode } from './ai'
import type { Channel, Message } from './chat'

export const CHAT_SOURCE_MESSAGE_CAP = 100
export const CHAT_SOURCE_CHAR_CAP = 20_000

export type AiChatSourceMessage = {
  messageId: string
  authorId: string
  authorName: string
  timestampMs: number
  textContent: string
  attachmentCount?: number
}

export type DataContextKind =
  | 'spreadsheet'
  | 'document'
  | 'gsheet'
  | 'link'
  | 'asset'
  | 'upload'
  | 'chat'
  | 'form'
  | 'form_responses'
  | 'design'

export type DataContext = {
  id: string
  kind: DataContextKind
  label: string
  sizeBytes?: number
  text: string
  fileId?: string
  mimeType?: string
  channelId?: string
  channelName?: string
  messages?: AiChatSourceMessage[]
  formId?: string
  formResponseIds?: string[]
  formResponseCount?: number
  fixedIngestCr?: number
  estimatedTokenCr?: { low: number; high: number }
}

export type AiChatSourceContext = {
  id: string
  channelId: string
  channelName: string
  messages: AiChatSourceMessage[]
  addedAt: number
}

export type ChatSelectionToken =
  | { kind: 'message'; channelId: string; channelName: string; messageId: string; authorName: string; preview: string }
  | { kind: 'channel'; channelId: string; channelName: string; sinceMs?: number }
  | { kind: 'tag'; tag: string; scope: 'workspace' | 'channel'; channelId?: string }

export type AiChatHandoffTarget =
  | { kind: 'new'; mode: AiOperatingMode }
  | { kind: 'existing'; sessionId: string }

export type AiChatHandoffRequest = {
  id: number
  attachments: Array<{
    id: string
    kind: 'chat'
    label: string
    text?: string
    channelId?: string
    channelName?: string
    messages?: AiChatSourceMessage[]
  }>
  mode?: AiOperatingMode
  targetSessionId?: string
}

export const timestampMillis = (timestamp: unknown): number => {
  if (typeof timestamp === 'number' && Number.isFinite(timestamp)) return timestamp
  if (timestamp instanceof Date) return timestamp.getTime()
  if (timestamp && typeof timestamp === 'object' && 'toMillis' in timestamp) {
    try {
      const value = (timestamp as { toMillis: () => number }).toMillis()
      return Number.isFinite(value) ? value : 0
    } catch {
      return 0
    }
  }
  return 0
}

const normalizeText = (value: string, cap = 180) => {
  const trimmed = value.replace(/\s+/g, ' ').trim()
  return trimmed.length > cap ? `${trimmed.slice(0, cap - 1)}...` : trimmed
}

export const chatSelectionTokenKey = (token: ChatSelectionToken): string => {
  if (token.kind === 'message') return `message:${token.channelId}:${token.messageId}`
  if (token.kind === 'channel') return `channel:${token.channelId}:${token.sinceMs ?? 'latest'}`
  return `tag:${token.scope}:${token.channelId ?? 'workspace'}:${token.tag.toLowerCase()}`
}

export type ChatSelectionState = {
  mode: 'list' | 'select'
  tokens: ChatSelectionToken[]
}

export type ChatSelectionAction =
  | { type: 'set-mode'; mode: ChatSelectionState['mode'] }
  | { type: 'toggle-token'; token: ChatSelectionToken }
  | { type: 'remove-token'; key: string }
  | { type: 'clear' }

export const chatSelectionInitialState: ChatSelectionState = { mode: 'list', tokens: [] }

export const chatSelectionReducer = (
  state: ChatSelectionState,
  action: ChatSelectionAction,
): ChatSelectionState => {
  if (action.type === 'set-mode') return { ...state, mode: action.mode }
  if (action.type === 'clear') return { ...state, tokens: [] }
  if (action.type === 'remove-token') {
    return { ...state, tokens: state.tokens.filter((token) => chatSelectionTokenKey(token) !== action.key) }
  }
  const key = chatSelectionTokenKey(action.token)
  const exists = state.tokens.some((token) => chatSelectionTokenKey(token) === key)
  return {
    ...state,
    tokens: exists
      ? state.tokens.filter((token) => chatSelectionTokenKey(token) !== key)
      : [...state.tokens, action.token],
  }
}

export const messageToChatSourceMessage = (message: Message): AiChatSourceMessage => ({
  messageId: message.id,
  authorId: message.authorId,
  authorName: message.authorName,
  timestampMs: timestampMillis(message.timestamp),
  textContent: message.textContent,
  attachmentCount: message.attachments?.length,
})

export const buildMessageToken = (message: Message, channel: Channel): ChatSelectionToken => ({
  kind: 'message',
  channelId: channel.id,
  channelName: channel.slug || channel.name,
  messageId: message.id,
  authorName: message.authorName,
  preview: normalizeText(message.textContent, 90),
})

export const buildChannelToken = (channel: Channel): ChatSelectionToken => ({
  kind: 'channel',
  channelId: channel.id,
  channelName: channel.slug || channel.name,
})

export const buildTagToken = (tag: string, channelId?: string): ChatSelectionToken => ({
  kind: 'tag',
  tag: tag.replace(/^#/, '').toLowerCase(),
  scope: channelId ? 'channel' : 'workspace',
  channelId,
})

const messageHasTag = (message: Message, tag: string) =>
  (message.tags ?? []).map((entry) => entry.toLowerCase()).includes(tag.toLowerCase())
  || new RegExp(`(^|\\s)#${tag.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}(?=$|\\s|[.,!?;:])`, 'i').test(message.textContent)

export const materializeChatSelectionTokens = (
  tokens: ChatSelectionToken[],
  input: {
    channels: Channel[]
    messagesByChannel: Record<string, Message[]>
    now?: number
  },
): AiChatSourceContext[] => {
  const channelById = new Map(input.channels.map((channel) => [channel.id, channel]))
  const grouped = new Map<string, AiChatSourceContext>()
  let remainingChars = CHAT_SOURCE_CHAR_CAP

  const addMessage = (channelId: string, message: Message, fallbackName?: string) => {
    if (message.isDeleted || !message.textContent.trim()) return
    const text = message.textContent.trim()
    if (remainingChars <= 0) return
    const clipped = text.length > remainingChars ? text.slice(0, remainingChars) : text
    remainingChars -= clipped.length
    const channel = channelById.get(channelId)
    const channelName = fallbackName || channel?.slug || channel?.name || channelId
    const current = grouped.get(channelId) ?? {
      id: `chat:${channelId}:${input.now ?? Date.now()}`,
      channelId,
      channelName,
      messages: [],
      addedAt: input.now ?? Date.now(),
    }
    if (current.messages.some((entry) => entry.messageId === message.id)) return
    current.messages.push({ ...messageToChatSourceMessage(message), textContent: clipped })
    current.messages = current.messages
      .sort((a, b) => a.timestampMs - b.timestampMs)
      .slice(-CHAT_SOURCE_MESSAGE_CAP)
    grouped.set(channelId, current)
  }

  tokens.forEach((token) => {
    if (token.kind === 'message') {
      const message = (input.messagesByChannel[token.channelId] ?? []).find((candidate) => candidate.id === token.messageId)
      if (message) addMessage(token.channelId, message, token.channelName)
      return
    }
    if (token.kind === 'channel') {
      ;(input.messagesByChannel[token.channelId] ?? []).slice(-CHAT_SOURCE_MESSAGE_CAP).forEach((message) => {
        if (!token.sinceMs || timestampMillis(message.timestamp) >= token.sinceMs) addMessage(token.channelId, message, token.channelName)
      })
      return
    }
    Object.entries(input.messagesByChannel).forEach(([channelId, messages]) => {
      if (token.scope === 'channel' && token.channelId && channelId !== token.channelId) return
      messages.filter((message) => messageHasTag(message, token.tag)).forEach((message) => addMessage(channelId, message))
    })
  })

  return Array.from(grouped.values()).filter((context) => context.messages.length > 0)
}

export const serializeChatSourceContext = (context: AiChatSourceContext): string => {
  const lines = context.messages.map((message) => {
    const when = message.timestampMs ? new Date(message.timestampMs).toLocaleString() : 'time unknown'
    return `[${message.authorName} - ${when}] ${message.textContent.replace(/\s+/g, ' ').trim()}`
  })
  return `--- Chat context: #${context.channelName} ---\n${lines.join('\n')}`
}

export const chatSourceContextToAttachment = (context: AiChatSourceContext) => ({
  id: context.id,
  kind: 'chat' as const,
  label: `${context.messages.length} chat${context.messages.length === 1 ? '' : 's'} - #${context.channelName}`,
  text: serializeChatSourceContext(context),
  channelId: context.channelId,
  channelName: context.channelName,
  messages: context.messages,
})
